// Input contexts (decision 0032; suggestion "an input contract"): named, layered binding sets (a game's might be
// exploring, combat, menu, dialogue). Active contexts are read from the highest priority down. Each one's actions
// read live; a control a higher context binds is taken from the contexts below it (so dialogue's Space doesn't
// also jump); a context that `blocks` hides every context below it (a menu over play). Base contexts are always on;
// the game pushes and pops the rest. Applying a stack recompiles the `Actions` map and its active-action filter.
//
// Frame-level presses: `on(action, fn)` hears a device press of an active action the moment it happens, for things
// that act while the sim is paused (open the pause menu, a menu's confirm). Gameplay reads `Actions` per step.
import type { Actions } from "./actions";
import { compileContract, shapeOf, type BindingDecl, type InputContract } from "./contract";
import type { Controls } from "./controls";

export class InputContexts {
  private contract: InputContract;
  private readonly actions: Actions;
  private pushed = new Set<string>();
  private live = new Set<string>();
  /** control → active button-like actions bound to it, for frame-level presses. */
  private byControl = new Map<string, string[]>();
  private listeners = new Map<string, Set<() => void>>();
  private changeFns = new Set<(active: string[]) => void>();
  private readonly off: () => void;

  /** Applies the contract's base contexts at once. Frame-level presses come from `controls` (default the actions'). */
  constructor(contract: InputContract, actions: Actions, controls: Pick<Controls, "onPress"> = actions.controls) {
    this.contract = contract;
    this.actions = actions;
    this.off = controls.onPress((c) => this.dispatch(c));
    this.apply();
  }

  /** Swap the contract (the rebinding model's effective one) and re-apply the stack. */
  setContract(contract: InputContract): void {
    this.contract = contract;
    this.apply();
  }

  /** Turn a context on (idempotent). Unknown ids throw: a typo would silently block nothing. */
  push(id: string): void {
    this.known(id);
    if (this.pushed.has(id)) return;
    this.pushed.add(id);
    this.apply();
  }

  /** Turn a context off (idempotent). */
  pop(id: string): void {
    this.known(id);
    if (!this.pushed.delete(id)) return;
    this.apply();
  }

  /** Push or pop. */
  set(id: string, on: boolean): void {
    if (on) this.push(id);
    else this.pop(id);
  }

  /** The contexts in force, highest priority first, stopping at the first that blocks. */
  active(): string[] {
    const on = this.contract.contexts.filter((c) => c.base || this.pushed.has(c.id)).sort((a, b) => b.priority - a.priority);
    const out: string[] = [];
    for (const c of on) {
      out.push(c.id);
      if (c.blocks) break;
    }
    return out;
  }

  /** Whether an action reads live under the current stack. */
  isActive(action: string): boolean {
    return this.live.has(action);
  }

  /** Hear a device press of `action` while it is active, synchronously. Returns the remover. */
  on(action: string, fn: () => void): () => void {
    const set = this.listeners.get(action) ?? new Set();
    set.add(fn);
    this.listeners.set(action, set);
    return () => set.delete(fn);
  }

  /** Hear every change of the active stack. Returns the remover. */
  onChange(fn: (active: string[]) => void): () => void {
    this.changeFns.add(fn);
    return () => this.changeFns.delete(fn);
  }

  /** The pushed (non-base) contexts, for a save. */
  snapshot(): string[] {
    return [...this.pushed];
  }

  restore(pushed: readonly string[]): void {
    this.pushed = new Set(pushed.filter((id) => this.contract.contexts.some((c) => c.id === id)));
    this.apply();
  }

  /** Stop listening to the controls. */
  dispose(): void {
    this.off();
  }

  /** Recompile the actions' map for the current stack. */
  apply(): void {
    const claimed = new Set<string>();
    const live = new Set<string>();
    const kept: BindingDecl[] = [];
    const byControl = new Map<string, string[]>();
    const active = this.active();
    for (const ctx of active) {
      const acts = this.contract.actions.filter((a) => a.context === ctx);
      const mine: string[] = [];
      for (const a of acts) {
        live.add(a.id);
        if (a.shares !== undefined) continue; // it reads the shared action's kept controls (compileContract)
        for (const b of this.contract.bindings) {
          if (b.action !== a.id) continue;
          const shape = shapeOf(b, a.type);
          const controls = shape === "dpad" ? b.controls.map((c) => (claimed.has(c) ? "" : c)) : b.controls.filter((c) => !claimed.has(c));
          kept.push({ ...b, controls });
          mine.push(...b.controls.filter((c) => c !== ""));
          if (shape === "button") {
            const sharers = acts.filter((x) => x.shares === a.id).map((x) => x.id);
            for (const c of controls) if (c) byControl.set(c, [...(byControl.get(c) ?? []), a.id, ...sharers]);
          }
        }
      }
      for (const c of mine) claimed.add(c);
    }
    // inactive actions keep their full bindings in the map (they read idle through the filter, and their deltas
    // still drain, so motion made under a menu doesn't swing the camera when it closes)
    for (const b of this.contract.bindings) if (!live.has(b.action)) kept.push(b);
    this.actions.setMap(compileContract(this.contract.actions, kept));
    this.actions.setActive((a) => live.has(a));
    this.live = live;
    this.byControl = byControl;
    for (const f of this.changeFns) f(active);
  }

  private known(id: string): void {
    if (!this.contract.contexts.some((c) => c.id === id)) throw new Error(`unknown input context "${id}" (have ${this.contract.contexts.map((c) => c.id).join(", ")})`);
  }

  private dispatch(control: string): void {
    for (const a of this.byControl.get(control) ?? []) for (const f of this.listeners.get(a) ?? []) f();
  }
}
